import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { Bell, Check } from 'lucide-react';
import { notificationsApi, workspacesApi, type Notification } from '@/lib/api';
import { useAppStore } from '@/stores/appStore';
import { formatRelativeTime } from '@/lib/utils';

function isInternalLink(link: string | null): link is string {
  return !!link && link.startsWith('/') && !link.startsWith('//');
}

export function NotificationDropdown() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const { currentAgency, currentWorkspace, setCurrentWorkspace } = useAppStore();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const { data: countData } = useQuery({
    queryKey: ['notifications-count', currentAgency?.id],
    queryFn: async () => {
      const token = await getToken();
      return notificationsApi.unreadCount(token!, currentAgency!.id);
    },
    enabled: !!currentAgency,
    refetchInterval: 30_000,
  });

  const { data: notifsData } = useQuery({
    queryKey: ['notifications', currentAgency?.id],
    queryFn: async () => {
      const token = await getToken();
      return notificationsApi.list(token!, currentAgency!.id);
    },
    enabled: !!currentAgency && open,
  });

  const markAllRead = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      return notificationsApi.markAllRead(token!, currentAgency!.id);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications', currentAgency?.id] });
      qc.invalidateQueries({ queryKey: ['notifications-count', currentAgency?.id] });
    },
  });

  const markOne = useMutation({
    mutationFn: async (id: string) => {
      const token = await getToken();
      return notificationsApi.markRead(token!, id);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications', currentAgency?.id] });
      qc.invalidateQueries({ queryKey: ['notifications-count', currentAgency?.id] });
    },
  });

  // Follow the notification's in-app link (e.g. /publishing?tab=pending) and mark it read.
  // Notifications are agency-wide but their pages are per-brand: a `workspace`
  // param names the brand the notification is about, so switch to it first.
  const openNotification = async (n: Notification) => {
    if (!isInternalLink(n.link)) return;
    if (!n.read_at) markOne.mutate(n.id);
    setOpen(false);
    const url = new URL(n.link, window.location.origin);
    const workspaceId = url.searchParams.get('workspace');
    url.searchParams.delete('workspace');
    if (workspaceId && workspaceId !== currentWorkspace?.id) {
      try {
        const token = await getToken();
        const { workspace } = await workspacesApi.get(token!, workspaceId);
        if (workspace.agency_id === currentAgency?.id) setCurrentWorkspace(workspace);
      } catch {
        // No access to that brand any more (or it was deleted): open the page
        // in the current brand rather than failing the click.
      }
    }
    navigate(url.pathname + url.search + url.hash);
  };

  const unreadCount = countData?.count ?? 0;
  const notifications = notifsData?.notifications ?? [];

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="relative flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground ring-1 ring-inset ring-hairline transition-[background-color,color,transform] duration-fast ease-sfx hover:bg-tertiary/70 hover:text-foreground active:scale-[0.94]"
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[9px] font-bold leading-none text-accent-foreground tabular-nums ring-2 ring-canvas">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="sfx-pop sfx-pop-down-right absolute right-0 top-full z-50 mt-2 w-80 overflow-hidden rounded-l bg-popover shadow-kumo-xl ring-1 ring-hairline">
          <div className="flex items-center justify-between border-b border-hairline px-4 py-3">
            <span className="font-display text-sm font-semibold text-foreground">Notifications</span>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => markAllRead.mutate()}
                className="rounded-full px-2 py-1 text-xs font-medium text-accent transition-colors duration-fast hover:bg-accent/10"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto divide-y divide-hairline">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <span className="sfx-icon-tile mx-auto mb-3 h-9 w-9">
                  <Bell className="h-4 w-4" />
                </span>
                <p className="text-xs font-medium text-foreground">You're all caught up</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Approvals, failed posts and mentions show up here.
                </p>
              </div>
            ) : (
              notifications.map((n: Notification) => (
                <div
                  key={n.id}
                  className={`px-4 py-3 flex gap-3 items-start ${!n.read_at ? 'bg-accent/[0.05]' : ''}`}
                >
                  {!n.read_at && (
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-accent shrink-0" aria-label="Unread" />
                  )}
                  <button
                    type="button"
                    onClick={() => openNotification(n)}
                    disabled={!isInternalLink(n.link)}
                    className={`group flex-1 min-w-0 text-left disabled:cursor-default ${n.read_at ? 'pl-3.5' : ''}`}
                  >
                    <p className={`text-xs font-medium text-foreground leading-snug transition-colors duration-fast ${isInternalLink(n.link) ? 'group-hover:text-accent' : ''}`}>{n.title}</p>
                    {n.body && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>
                    )}
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {formatRelativeTime(n.created_at)}
                    </p>
                  </button>
                  {!n.read_at && (
                    <button
                      type="button"
                      onClick={() => markOne.mutate(n.id)}
                      className="shrink-0 rounded-full p-1 text-muted-foreground transition-colors duration-fast hover:bg-tertiary/70 hover:text-foreground"
                      title="Mark as read"
                      aria-label="Mark as read"
                    >
                      <Check className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
